import { api_fetch } from "../../../api/client"
import { download_export } from "../../../utils"
import {
  DistributorSalesReport,
  UploadResponse,
  ChartDataResponse,
  HierarchyResponse,
  ListReportsQuery,
  ChartDataQuery,
  HierarchyQuery,
  HierarchyExportQuery,
  DistributorSource,
  ReportType,
  ValuationMethod,
  SkuSummaryResponse,
  SkuSummaryQuery,
  RegionBreakdownResponse,
  RegionBreakdownQuery,
  DeductionOverlayResponse,
  DeductionOverlayQuery,
  DcSummaryResponse,
  DcSummaryQuery,
  RetailerProfitabilityResponse,
  HierarchyBreakdownResponse,
  SpinsOverviewResponse,
  SpinsBrandsResponse,
  SpinsBrandComparisonResponse,
  SpinsProductsResponse,
  BrandWeeklyMovementResponse,
  ProfitabilityCostResponse,
  RetailerAliasRowJson,
  RetailerAliasSuggestPreviewResponse,
  RetailerAliasSuggestPair,
  RetailerMappingCandidatesResponse,
} from "../types/distributor_sales"

const API_BASE = '/distributor-sales-reports'
const DATA_BASE = '/distributor-sales-data'

/**
 * Upload a distributor sales report
 */
export async function uploadDistributorReport(
  file: File,
  source: DistributorSource,
  reportDate: string,
  startDate?: string,
  endDate?: string,
  reportType?: ReportType,
  valuationMethod?: ValuationMethod,
  uploadBrandName?: string
): Promise<UploadResponse> {
  const formData = new FormData()
  formData.append('file', file)
  formData.append('source', source)
  formData.append('report_date', reportDate)
  if (startDate) {
    formData.append('start_date', startDate)
  }
  if (endDate) {
    formData.append('end_date', endDate)
  }
  if (reportType) {
    formData.append('report_type', reportType)
  }
  if (valuationMethod) {
    formData.append('valuation_method', valuationMethod)
  }
  if (uploadBrandName) {
    formData.append('upload_brand_name', uploadBrandName)
  }

  // Use native fetch for FormData uploads
  const response = await fetch(`/api${API_BASE}/upload`, {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Failed to upload report: ${error}`)
  }

  return response.json()
}

export async function getSpinsOverview(query: {
  report_id: string
  weeks?: number
  geography?: string
  brand?: string
}): Promise<SpinsOverviewResponse> {
  const params = new URLSearchParams()
  params.append('report_id', query.report_id)
  if (query.weeks) params.append('weeks', query.weeks.toString())
  if (query.geography) params.append('geography', query.geography)
  if (query.brand) params.append('brand', query.brand)
  const result = await api_fetch<SpinsOverviewResponse>(`${DATA_BASE}/spins/overview?${params}`, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch syndicated data overview: ${result.error.message}`)
}

export async function getSpinsBrands(query: {
  report_id: string
  weeks?: number
  geography?: string
  query?: string
}): Promise<SpinsBrandsResponse> {
  const params = new URLSearchParams()
  params.append('report_id', query.report_id)
  if (query.weeks) params.append('weeks', query.weeks.toString())
  if (query.geography) params.append('geography', query.geography)
  if (query.query) params.append('query', query.query)
  const result = await api_fetch<SpinsBrandsResponse>(`${DATA_BASE}/spins/brands?${params}`, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch syndicated data brands: ${result.error.message}`)
}

export async function getSpinsBrandComparison(query: {
  report_id: string
  weeks?: number
  geography?: string
  brands?: string[]
}): Promise<SpinsBrandComparisonResponse> {
  const params = new URLSearchParams()
  params.append('report_id', query.report_id)
  if (query.weeks) params.append('weeks', query.weeks.toString())
  if (query.geography) params.append('geography', query.geography)
  if (query.brands?.length) params.append('brands', query.brands.join(','))
  const result = await api_fetch<SpinsBrandComparisonResponse>(`${DATA_BASE}/spins/brand-comparison?${params}`, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch syndicated data brand comparison: ${result.error.message}`)
}

export async function getSpinsProducts(query: {
  report_id: string
  weeks?: number
  geography?: string
  brand?: string
}): Promise<SpinsProductsResponse> {
  const params = new URLSearchParams()
  params.append('report_id', query.report_id)
  if (query.weeks) params.append('weeks', query.weeks.toString())
  if (query.geography) params.append('geography', query.geography)
  if (query.brand) params.append('brand', query.brand)
  const result = await api_fetch<SpinsProductsResponse>(`${DATA_BASE}/spins/products?${params}`, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch syndicated data products: ${result.error.message}`)
}

export async function getBrandWeeklyMovement(query: {
  report_id: string
  geography?: string
}): Promise<BrandWeeklyMovementResponse> {
  const params = new URLSearchParams()
  params.append('report_id', query.report_id)
  if (query.geography) params.append('geography', query.geography)
  const result = await api_fetch<BrandWeeklyMovementResponse>(`${DATA_BASE}/spins/brand-weekly-movement?${params}`, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch brand weekly movement data: ${result.error.message}`)
}

/**
 * List all uploaded reports
 */
export async function listDistributorReports(
  query?: ListReportsQuery
): Promise<DistributorSalesReport[]> {
  const params = new URLSearchParams()
  if (query?.source) params.append('source', query.source)
  if (query?.limit) params.append('limit', query.limit.toString())

  const url = params.toString() ? `${API_BASE}?${params}` : API_BASE
  
  const result = await api_fetch<DistributorSalesReport[]>(url, {
    method: 'GET',
  })
  
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to fetch reports: ${result.error.message}`)
  }
}

/**
 * Delete a report
 */
export async function deleteDistributorReport(reportId: string): Promise<void> {
  const result = await api_fetch(`${API_BASE}/${reportId}`, {
    method: 'DELETE',
  })
  
  if (!result.ok) {
    throw new Error(`Failed to delete report: ${result.error.message}`)
  }
}

/**
 * Get chart data for visualization
 */
export async function getChartData(query?: ChartDataQuery): Promise<ChartDataResponse> {
  const params = new URLSearchParams()
  if (query?.source) params.append('source', query.source)
  if (query?.retailers) params.append('retailers', query.retailers)
  if (query?.top_n) params.append('top_n', query.top_n.toString())
  if (query?.skus) params.append('skus', query.skus)
  if (query?.by_sku) params.append('by_sku', 'true')

  const url = params.toString() ? `${DATA_BASE}/chart-data?${params}` : `${DATA_BASE}/chart-data`
  
  const result = await api_fetch<ChartDataResponse>(url, {
    method: 'GET',
  })
  
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to fetch chart data: ${result.error.message}`)
  }
}

/**
 * Get SKU summary for pie chart and SKU filter list
 */
export async function getSkuSummary(query?: SkuSummaryQuery): Promise<SkuSummaryResponse> {
  const params = new URLSearchParams()
  if (query?.source) params.append('source', query.source)
  if (query?.report_id) params.append('report_id', query.report_id)

  const url = params.toString() ? `${DATA_BASE}/sku-summary?${params}` : `${DATA_BASE}/sku-summary`
  const result = await api_fetch<SkuSummaryResponse>(url, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch SKU summary: ${result.error.message}`)
}

/**
 * Pie chart data: direct children of a hierarchy node (same report)
 */
export async function getHierarchyBreakdown(query: {
  report_id: string
  node_id: string
}): Promise<HierarchyBreakdownResponse> {
  const params = new URLSearchParams()
  params.append('report_id', query.report_id)
  params.append('node_id', query.node_id)
  const url = `${DATA_BASE}/hierarchy-breakdown?${params}`
  const result = await api_fetch<HierarchyBreakdownResponse>(url, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch hierarchy breakdown: ${result.error.message}`)
}

/**
 * Get region breakdown for a specific retailer (for the region pie chart)
 */
export async function getRegionBreakdown(query: RegionBreakdownQuery): Promise<RegionBreakdownResponse> {
  const params = new URLSearchParams()
  if (query.report_id) params.append('report_id', query.report_id)
  if (query.retailer_parent) params.append('retailer_parent', query.retailer_parent)
  if (query.source) params.append('source', query.source)

  const url = `${DATA_BASE}/region-breakdown?${params}`
  const result = await api_fetch<RegionBreakdownResponse>(url, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch region breakdown: ${result.error.message}`)
}

/**
 * Get deduction amounts by retailer, fuzzy-matched against sales data retailers
 */
export async function getDeductionOverlay(query?: DeductionOverlayQuery): Promise<DeductionOverlayResponse> {
  const params = new URLSearchParams()
  if (query?.start_date) params.append('start_date', query.start_date)
  if (query?.end_date) params.append('end_date', query.end_date)
  if (query?.source) params.append('source', query.source)
  if (query?.report_id) params.append('report_id', query.report_id)

  const url = params.toString()
    ? `${DATA_BASE}/deduction-overlay?${params}`
    : `${DATA_BASE}/deduction-overlay`
  const result = await api_fetch<DeductionOverlayResponse>(url, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch deduction overlay: ${result.error.message}`)
}

/**
 * Get per-retailer profitability combining sales data with fuzzy-matched deductions
 */
export async function getRetailerProfitability(query?: {
  source?: string
  report_id?: string
}): Promise<RetailerProfitabilityResponse> {
  const params = new URLSearchParams()
  if (query?.source) params.append('source', query.source)
  if (query?.report_id) params.append('report_id', query.report_id)

  const url = params.toString()
    ? `${DATA_BASE}/retailer-profitability?${params}`
    : `${DATA_BASE}/retailer-profitability`
  const result = await api_fetch<RetailerProfitabilityResponse>(url, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch retailer profitability: ${result.error.message}`)
}

export async function getProfitabilityCost(source: string): Promise<ProfitabilityCostResponse> {
  const params = new URLSearchParams({ source })
  const result = await api_fetch<ProfitabilityCostResponse>(`${DATA_BASE}/profitability-cost?${params}`, {
    method: 'GET',
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch profitability cost: ${result.error.message}`)
}

export async function patchProfitabilityCost(body: {
  source: string
  selling_cost_pct?: string | number | null
  selling_cost_overrides?: Record<string, number | string>
}): Promise<ProfitabilityCostResponse> {
  const result = await api_fetch<ProfitabilityCostResponse>(`${DATA_BASE}/profitability-cost`, {
    method: 'PATCH',
    body,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to save profitability cost: ${result.error.message}`)
}

export async function listRetailerAliases(report_id: string): Promise<RetailerAliasRowJson[]> {
  const params = new URLSearchParams({ report_id })
  const result = await api_fetch<RetailerAliasRowJson[]>(`${DATA_BASE}/retailer-aliases?${params}`, {
    method: 'GET',
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to list retailer aliases: ${result.error.message}`)
}

export async function getRetailerMappingCandidates(report_id: string): Promise<RetailerMappingCandidatesResponse> {
  const params = new URLSearchParams({ report_id })
  const result = await api_fetch<RetailerMappingCandidatesResponse>(
    `${DATA_BASE}/retailer-mapping-candidates?${params}`,
    { method: 'GET' }
  )
  if (result.ok) return result.value.data
  throw new Error(`Failed to load retailer mapping candidates: ${result.error.message}`)
}

export async function upsertRetailerAlias(body: {
  source: string
  report_id?: string | null
  deduction_retailer_alias: string
  sales_retailer_name: string
  notes?: string | null
}): Promise<RetailerAliasRowJson> {
  const result = await api_fetch<RetailerAliasRowJson>(`${DATA_BASE}/retailer-aliases`, {
    method: 'POST',
    body,
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to save retailer alias: ${result.error.message}`)
}

export async function deleteRetailerAlias(id: string): Promise<void> {
  const result = await api_fetch<unknown>(`${DATA_BASE}/retailer-aliases/${id}`, { method: 'DELETE' })
  if (result.ok) return
  throw new Error(`Failed to delete retailer alias: ${result.error.message}`)
}

export async function suggestRetailerAliasPreview(report_id: string): Promise<RetailerAliasSuggestPreviewResponse> {
  const result = await api_fetch<RetailerAliasSuggestPreviewResponse>(
    `${DATA_BASE}/retailer-aliases/suggest-preview`,
    { method: 'POST', body: { report_id } }
  )
  if (result.ok) return result.value.data
  throw new Error(`Failed to preview retailer alias suggestions: ${result.error.message}`)
}

export async function suggestRetailerAliasApply(
  report_id: string,
  pairs: RetailerAliasSuggestPair[]
): Promise<RetailerAliasRowJson[]> {
  const result = await api_fetch<RetailerAliasRowJson[]>(`${DATA_BASE}/retailer-aliases/suggest-apply`, {
    method: 'POST',
    body: { report_id, pairs },
  })
  if (result.ok) return result.value.data
  throw new Error(`Failed to apply retailer alias suggestions: ${result.error.message}`)
}

/**
 * Get DC deduction summary for the map and trend chart (KeHE invoices only).
 */
export async function getDcSummary(query?: DcSummaryQuery): Promise<DcSummaryResponse> {
  const params = new URLSearchParams()
  params.append('source', query?.source ?? 'kehe')
  if (query?.start_date) params.append('start_date', query.start_date)
  if (query?.end_date) params.append('end_date', query.end_date)

  const url = `/deductions/dc-summary?${params}`
  const result = await api_fetch<DcSummaryResponse>(url, { method: 'GET' })
  if (result.ok) return result.value.data
  throw new Error(`Failed to fetch DC summary: ${result.error.message}`)
}

/**
 * Download the whole hierarchy (every level, down to store and SKU) as an XLSX file.
 */
export async function downloadHierarchyExport(query?: HierarchyExportQuery): Promise<void> {
  const params = new URLSearchParams()
  if (query?.source) params.append('source', query.source)
  if (query?.report_id) params.append('report_id', query.report_id)
  if (query?.include_deductions) params.append('include_deductions', 'true')

  const url = params.toString() ? `/api${DATA_BASE}/export?${params}` : `/api${DATA_BASE}/export`
  await download_export(url, 'sales_by_retailer.xlsx')
}

/**
 * Get hierarchical data for the expandable table
 */
export async function getHierarchyData(query?: HierarchyQuery): Promise<HierarchyResponse> {
  const params = new URLSearchParams()
  if (query?.source) params.append('source', query.source)
  if (query?.report_id) params.append('report_id', query.report_id)

  const url = params.toString() ? `${DATA_BASE}/hierarchy?${params}` : `${DATA_BASE}/hierarchy`
  
  const result = await api_fetch<HierarchyResponse>(url, {
    method: 'GET',
  })
  
  if (result.ok) {
    return result.value.data
  } else {
    throw new Error(`Failed to fetch hierarchy data: ${result.error.message}`)
  }
}
